import { StrictMode, type ReactElement } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { ScreenPair } from "./routes/ScreenPair";

import "./styles/index.css";
import { Shell } from "./Shell";
import { subscribeToSession } from "./auth/session";
import { EventProvider } from "./context/EventProvider";
import { SettingsProvider } from "./settings/context";
import { Home } from "./routes/Home";
import { Login } from "./routes/Login";
import { Registration } from "./routes/Registration";
import { Typology } from "./routes/Typology";
import { Articles } from "./routes/Articles";
import { Dates } from "./routes/Dates";
import { Informations } from "./routes/Informations";
import { Companions } from "./routes/Companions";
import { Company } from "./routes/Company";
import { Conferences } from "./routes/Conferences";
import { Confirmation } from "./routes/Confirmation";
import { NotFound } from "./routes/NotFound";
import { SitePage } from "./routes/SitePage";
import { Badge } from "./routes/Badge";
import { Programme } from "./routes/Programme";
import { Contacts } from "./routes/Contacts";
import { Scan } from "./routes/Scan";
import { Album } from "./routes/Album";
import { FormPage } from "./routes/FormPage";
import { Infos } from "./routes/Infos";
import { Live } from "./routes/Live";
import { Replay } from "./routes/Replay";
import { LiveQuestions } from "./routes/LiveQuestions";
import { LiveRegie } from "./routes/LiveRegie";
import { LiveScene } from "./routes/LiveScene";
import { adoptTokenFromUrl } from "./auth/link";
import { StepPage } from "./funnel/StepPage";
import { SITE_PAGES, pagePath } from "./pages/registry";
import { STANDALONE_STEPS, stepPageSlug } from "./pages/stepPages";

/**
 * THE SITE PAGES THAT REALLY EXIST — the list is HERE, and it is short.
 *
 * All twelve can be switched on in the panel today; their contents arrive one at a
 * time. Those without a screen yet serve `SitePage`, which SAYS SO. Adding a screen
 * means adding a line here — not touching the router.
 */
const BUILT: Record<string, ReactElement> = {
  badge: <Badge />,
  programme: <Programme />,
  contacts: <Contacts />,
  scan: <Scan />,
  album: <Album />,
  live: <Live />,
  replay: <Replay />,
  infos: <Infos />,
  form: <FormPage />,
};

/**
 * L'écran de chaque étape qui peut être servie comme page — monté en mode page.
 *
 * ⚠️ DÉCLARÉ ICI, PAS DEVINÉ DEPUIS LA CLÉ. Le registre nomme les étapes, il n'importe
 * aucun composant (le routeur le lit, et l'inverse ferait un cycle) : la table est le
 * seul endroit où les deux se rencontrent, et une clé sans écran serait une erreur de
 * compilation plutôt qu'une page blanche.
 */
const PAGE_STEPS: Record<string, ReactElement> = {
  informations: <Informations asPage />,
  company: <Company asPage />,
  companions: <Companions asPage />,
  /**
   * ⚠️ CES QUATRE-LÀ SONT ENVELOPPÉES, LES TROIS DU DESSUS NON. Elles écrivent la
   * PARTICIPATION, dont l'écriture déduit « vient / ne vient pas » de ce qu'elle trouve :
   * ouverte par quelqu'un qui n'a jamais répondu, la page l'aurait enregistré comme ne
   * participant pas, sans un mot. Le tunnel n'a pas ce trou, on n'y atteint pas les
   * dates sans avoir dit qu'on vient. Voir `funnel/StepPage.tsx`.
   *
   * ⚠️ LA PARTICIPATION S'OUVRE À UN REFUS (`answered`), LES TROIS AUTRES NON : c'est
   * par elle qu'on revient sur « je ne viens pas » (bug #522).
   */
  registration: (
    <StepPage answered>
      <Registration asPage />
    </StepPage>
  ),
  typology: (
    <StepPage>
      <Typology asPage />
    </StepPage>
  ),
  dates: (
    <StepPage>
      <Dates asPage />
    </StepPage>
  ),
  articles: (
    <StepPage>
      <Articles asPage />
    </StepPage>
  ),
};

/**
 * One cache instance for the whole application. Settings rarely change during a
 * visit, so we avoid refetching on window focus: it brings nothing and makes the
 * screen flicker.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

/**
 * ⚠️ UNE AUTRE SESSION, UN AUTRE CACHE. Les requêtes personnelles (« qui suis-je »,
 * mes droits, ma participation, mes contacts…) sont clefées sur un booléen « connecté »,
 * pas sur le jeton : A se déconnecte, B se connecte sur le même onglet dans les cinq
 * minutes, et B recevait le profil, la participation et le bouton « Configurer le site »
 * de A. Ce qui décrit l'événement lui-même (contexte, langues, typologies) ne dépend
 * de personne et reste ; tout le reste part avec la session.
 */
const SHARED = new Set(["context", "languages", "typos"]);
subscribeToSession(() => {
  queryClient.removeQueries({
    predicate: (q) => !SHARED.has(String(q.queryKey[0])),
  });
});

/**
 * The screens, declared ONCE and mounted twice: under `/:context/…` for a real
 * event, and at the root for the seeded demonstration. Writing the list twice is
 * how one branch quietly loses a screen.
 */
const screens = [
  { index: true, element: <Home /> },
  { path: "login", element: <Login /> },
  { path: "registration", element: <Registration /> },
  { path: "registration/typology", element: <Typology /> },
  { path: "registration/dates", element: <Dates /> },
  { path: "registration/articles", element: <Articles /> },
  { path: "registration/informations", element: <Informations /> },
  /**
   * ⚠️ UNE SEULE ROUTE POUR TOUTES LES INSTANCES. Les étapes « informations » d'un
   * événement vivent dans sa configuration, qui arrive APRÈS la construction du routeur :
   * une route par instance obligerait à le reconstruire quand elle atterrit. Un segment
   * dynamique évite la question, et l'écran lit son instance dedans.
   */
  { path: "registration/informations/:instance", element: <Informations /> },
  { path: "registration/company", element: <Company /> },
  { path: "registration/conferences", element: <Conferences /> },
  { path: "registration/companions", element: <Companions /> },
  { path: "registration/confirmation", element: <Confirmation /> },
  /**
   * ═══ LES DEUX OUTILS DU DIRECT ═══
   *
   * ⚠️ DES ROUTES STATIQUES, ET PAS DES PAGES DU SITE. Elles ne s'allument pas dans
   * « Pages du site » et n'apparaissent dans aucun menu, parce qu'elles ne
   * s'adressent pas au participant : la régie est refusée par le serveur à qui n'a
   * pas de rôle sur le domaine, et la salle s'authentifie par un jeton d'écran.
   *
   * ⚠️ SOUS `/live/…`, DONC APRÈS `/live` QUI EST UNE PAGE DU SITE. Les deux ne se
   * disputent rien : `live` est un segment, `live/regie` en fait deux.
   */
  { path: "live/regie", element: <LiveRegie /> },
  { path: "live/questions", element: <LiveQuestions /> },
  { path: "live/scene", element: <LiveScene /> },
  /**
   * ═══ THE SITE'S PAGES ═══ switched on in "Le site → Pages du site".
   *
   * ⚠️ GENERATED FROM THE REGISTRY, AND STATIC. A single `:page` would have been one
   * line shorter and would have broken every event address: the screens are mounted
   * under `/:context` AND at the root, so `/:page` would be `/:context`'s direct rival
   * there — two patterns of one dynamic segment, same score, and the declaration order
   * would have decided whether `/mon-salon` names an event or a page. Twelve static
   * routes never raise the question.
   *
   * ⚠️ AND THEIR ADDRESS COMES FROM `pagePath`, never retyped here: that is what the
   * navigation, the tiles and the screen picker read. Two ways of writing one address
   * is a menu pointing beside its page.
   */
  /**
   * ═══ LES ÉTAPES SERVIES COMME PAGES ═══
   *
   * ⚠️ LE MÊME COMPOSANT, MONTÉ AILLEURS — et c'est tout ce que « page » veut dire ici.
   * `asPage` lui dit qu'il n'est pas dans le tunnel : pas de fil d'Ariane, pas de
   * « Continuer » vers une étape suivante, un bouton qui enregistre et rend la main.
   * Une seconde copie de l'écran aurait divergé de la première dès le premier
   * changement, et l'organisateur aurait réglé deux fois la même chose.
   *
   * ⚠️ STATIQUES, ET POUR LA RAISON ÉCRITE JUSTE EN DESSOUS : au premier niveau, un
   * segment dynamique serait le RIVAL de `/:context`. L'instance, elle, est un second
   * segment — elle ne concurrence rien.
   */
  /* ⚠️ L'ADRESSE VIENT DE `stepPageSlug`, JAMAIS DE LA CLÉ : la page de participation
     vit à `/participation`, sa clé vaut `registration` — celle du tunnel. Montée sur la
     clé, elle aurait rivalisé avec la première étape, et perdu en silence. */
  ...STANDALONE_STEPS.flatMap((step) => [
    { path: stepPageSlug(step), element: PAGE_STEPS[step.key] },
    ...(step.repeatable
      ? [
          {
            path: `${stepPageSlug(step)}/:instance`,
            element: PAGE_STEPS[step.key],
          },
        ]
      : []),
  ]),
  ...SITE_PAGES.flatMap((page) => [
    {
      path: pagePath(page).slice(1),
      element: BUILT[page.key] ?? <SitePage pageKey={page.key} />,
    },
    /**
     * ⚠️ UNE SEULE ROUTE POUR TOUS LES EXEMPLAIRES d'une page multipliable, même
     * raison que l'étape répétable juste au-dessus : combien il en existe vit dans la
     * configuration, qui arrive après le routeur. Et l'instance est un SECOND segment :
     * elle ne rivalise pas avec `/:context`.
     */
    ...(page.repeatable
      ? [
          {
            path: `${pagePath(page).slice(1)}/:instance`,
            element: BUILT[page.key] ?? <SitePage pageKey={page.key} />,
          },
        ]
      : []),
  ]),
  // Screens land in phase 2. Any unknown address falls here — V1 has NO error
  // page at all: a wrong link there produces raw text outside the theme.
  { path: "*", element: <NotFound /> },
];

/**
 * ⚠️ THE ORDER IS THE POINT. `EventProvider` needs the router (it reads the
 * context from the URL); `SettingsProvider` needs the event (it saves to it).
 * Settings used to sit outside the router entirely, which is exactly why they
 * could never be saved anywhere.
 */
const shell = (
  <EventProvider>
    <SettingsProvider>
      <Shell />
    </SettingsProvider>
  </EventProvider>
);

const router = createBrowserRouter([
  // The static branch is declared first for readability; React Router ranks
  // static segments above dynamic ones regardless, so `/login` is never
  // mistaken for an event token.
  /**
   * ⚠️ `/ecran` : LE POSTE DE LA SALLE, SANS ÉVÉNEMENT NI CHÂSSIS. On y tape le code
   * court d'un écran (`routes/ScreenPair.tsx`), qui désigne l'événement. Statique, il
   * passe devant `/:context` — un événement dont l'alias serait « ecran » n'y
   * répondrait plus.
   */
  { path: "/ecran", element: <ScreenPair /> },
  { path: "/", element: shell, children: screens },
  { path: "/:context", element: shell, children: screens },
]);

/**
 * ⚠️ AVANT TOUT LE RESTE. Un lien d'invitation — ou le bouton « Se connecter » d'une
 * fiche du back-office — porte la session dans son adresse. Rangée après le premier
 * rendu, la toute première requête (« qui est connecté ? ») serait partie sans elle, et
 * le visiteur atterrirait sur l'écran de connexion avec un jeton valide en poche. Voir
 * `auth/link.ts`, qui dit aussi pourquoi le jeton quitte la barre d'adresse aussitôt.
 */
adoptTokenFromUrl();

const root = document.getElementById("root");
if (!root) throw new Error("#root element not found in index.html");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
